import { computed, readonly, ref } from 'vue';

type PwaInstallOutcome = 'accepted' | 'dismissed' | 'installed' | 'unavailable';

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const installPrompt = ref<BeforeInstallPromptEvent | null>(null);
const isInstalled = ref(false);
const isReady = ref(false);
const lastOutcome = ref<PwaInstallOutcome | null>(null);
const INSTALL_PROMPT_SESSION_BYPASS_KEY = 'newty.pwaInstall.continueWithoutInstall';

let isListening = false;

function isBrowser() {
	return typeof window !== 'undefined' && typeof navigator !== 'undefined';
}

export function hasInstallPromptSessionBypass() {
	if (!isBrowser()) return false;
	return window.sessionStorage.getItem(INSTALL_PROMPT_SESSION_BYPASS_KEY) === '1';
}

export function dismissInstallPromptForSession() {
	if (!isBrowser()) return;
	window.sessionStorage.setItem(INSTALL_PROMPT_SESSION_BYPASS_KEY, '1');
}

export function clearInstallPromptSessionBypass() {
	if (!isBrowser()) return;
	window.sessionStorage.removeItem(INSTALL_PROMPT_SESSION_BYPASS_KEY);
}

export function isIosDevice() {
	if (!isBrowser()) return false;

	const navigatorWithTouch = window.navigator as Navigator & { maxTouchPoints?: number };
	const isClassicIos = /iphone|ipad|ipod/i.test(navigatorWithTouch.userAgent);
	const isModernIpad = navigatorWithTouch.platform === 'MacIntel' && (navigatorWithTouch.maxTouchPoints ?? 0) > 1;

	return isClassicIos || isModernIpad;
}

export function isMobileDevice() {
	if (!isBrowser()) return false;

	const navigatorWithTouch = window.navigator as Navigator & { maxTouchPoints?: number };
	const hasMobileUserAgent = /android|iphone|ipad|ipod|mobile|windows phone/i.test(navigatorWithTouch.userAgent);
	const isModernIpad = navigatorWithTouch.platform === 'MacIntel' && (navigatorWithTouch.maxTouchPoints ?? 0) > 1;

	return hasMobileUserAgent || isModernIpad;
}

export function isStandaloneDisplay() {
	if (!isBrowser()) return false;

	return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function fallbackInstallMessage() {
	if (isInstalled.value) return 'نسخه PWA نیوتی روی دستگاه شما نصب شده است.';

	if (isIosDevice()) {
		return 'برای نصب در iPhone یا iPad، از منوی Share مرورگر گزینه Add to Home Screen را انتخاب کنید.';
	}

	return 'اگر پنجره نصب نمایش داده نشد، از منوی مرورگر گزینه Install app یا Add to Home screen را انتخاب کنید.';
}

export function setupPwaInstallPrompt() {
	if (!isBrowser() || isListening) return;

	isInstalled.value = isStandaloneDisplay();
	isReady.value = true;
	isListening = true;

	const displayMode = window.matchMedia('(display-mode: standalone)');

	const handleDisplayModeChange = () => {
		isInstalled.value = isStandaloneDisplay();
	};

	const handleBeforeInstallPrompt = (event: Event) => {
		event.preventDefault();
		installPrompt.value = event as BeforeInstallPromptEvent;
		lastOutcome.value = null;
	};

	const handleAppInstalled = () => {
		installPrompt.value = null;
		isInstalled.value = true;
		lastOutcome.value = 'installed';
	};

	window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
	window.addEventListener('appinstalled', handleAppInstalled);
	displayMode.addEventListener?.('change', handleDisplayModeChange);
}

export function usePwaInstall() {
	setupPwaInstallPrompt();

	const canPromptInstall = computed(() => Boolean(installPrompt.value) && !isInstalled.value);
	const canShowInstallCta = computed(() => !isInstalled.value);

	const requestInstall = async (): Promise<PwaInstallOutcome> => {
		if (isInstalled.value) {
			lastOutcome.value = 'installed';
			return 'installed';
		}

		if (!installPrompt.value) {
			lastOutcome.value = 'unavailable';
			return 'unavailable';
		}

		const promptEvent = installPrompt.value;
		await promptEvent.prompt();
		const { outcome } = await promptEvent.userChoice;

		lastOutcome.value = outcome;
		installPrompt.value = null;

		return outcome;
	};

	return {
		canPromptInstall,
		canShowInstallCta,
		fallbackInstallMessage,
		isInstalled: readonly(isInstalled),
		isReady: readonly(isReady),
		lastOutcome: readonly(lastOutcome),
		requestInstall,
	};
}

export default usePwaInstall;
